import { warnDev } from '@/utils/warnDev'

import { COLUMN_CELL_TYPE, SORT_ORDER } from '../TableBase.constants'
import { Column, PathType, SortOrder } from '../TableBase.types'
import { getPropertyAtPath } from './getPropertyAtPath'

type SortedDataParams<Data, SortAttribute extends string> = {
  data: (Data & { __id: string })[]
  columns: Column<Data, SortAttribute>[]
  sortAttribute?: SortAttribute
  sortOrder?: SortOrder
}

export const getSortedData = <Data extends Record<string, unknown>, SortAttribute extends string>({
  data,
  columns,
  sortAttribute,
  sortOrder
}: SortedDataParams<Data, SortAttribute>) => {
  if (!data || !sortAttribute || !sortOrder) {
    return data
  }

  const column = columns.find((col) => col.sortAttribute === sortAttribute) as
    | Column<Data, SortAttribute>
    | undefined
  if (!column) return data

  const columnId = column.id as PathType<Data>

  return [...data].sort(({ __id, ...restA }, { __id: ___id, ...restB }) => {
    const a = restA as unknown as Data
    const b = restB as unknown as Data

    const dataAtPathA = getPropertyAtPath(a, columnId)
    const dataAtPathB = getPropertyAtPath(b, columnId)

    if (column.type === COLUMN_CELL_TYPE.COMPONENT && column.comparator) {
      const typedComparator = column.comparator as (
        a: typeof dataAtPathA,
        b: typeof dataAtPathB
      ) => number
      return sortOrder === SORT_ORDER.ASC
        ? typedComparator(dataAtPathA, dataAtPathB)
        : typedComparator(dataAtPathB, dataAtPathA)
    } else if (column.type === COLUMN_CELL_TYPE.COMPONENT) {
      warnDev('Cell: Column with type component should have a custom comparator function')
      return 0
    }

    let valueA, valueB

    switch (column.type) {
      case COLUMN_CELL_TYPE.TEXT:
      case COLUMN_CELL_TYPE.NUMBER: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        valueA = (column.transform?.(dataAtPathA as any) ?? dataAtPathA) || column?.noDataFallback
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        valueB = (column.transform?.(dataAtPathB as any) ?? dataAtPathB) || column?.noDataFallback
        break
      }

      case COLUMN_CELL_TYPE.DATE: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        valueA = (column.transform?.(dataAtPathA as any) ?? dataAtPathA) || column?.noDataFallback
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        valueB = (column.transform?.(dataAtPathB as any) ?? dataAtPathB) || column?.noDataFallback

        if (typeof valueA === 'string' || typeof valueA === 'number' || valueA instanceof Date) {
          valueA = new Date(valueA).getTime()
          if (isNaN(valueA)) valueA = 0
        }
        if (typeof valueB === 'string' || typeof valueB === 'number' || valueB instanceof Date) {
          valueB = new Date(valueB).getTime()
          if (isNaN(valueB)) valueB = 0
        }
        break
      }

      default: {
        const errorColumn: never = column
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        warnDev(`Cell: Unhandled column type: ${(errorColumn as any)?.type}`)
      }
    }

    if (valueA === valueB) return 0

    if (typeof valueA === 'string' && typeof valueB === 'string') {
      return sortOrder === SORT_ORDER.ASC
        ? valueA.localeCompare(valueB)
        : valueB.localeCompare(valueA)
    }

    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortOrder === SORT_ORDER.ASC ? valueA - valueB : valueB - valueA
    }

    if (typeof valueA === 'number' && typeof valueB === 'string') {
      return sortOrder === SORT_ORDER.ASC ? -1 : 1
    }

    if (typeof valueA === 'string' && typeof valueB === 'number') {
      return sortOrder === SORT_ORDER.ASC ? 1 : -1
    }

    if (valueA === undefined && valueB !== undefined) {
      return sortOrder === SORT_ORDER.ASC ? -1 : 1
    }

    if (valueA !== undefined && valueB === undefined) {
      return sortOrder === SORT_ORDER.ASC ? 1 : -1
    }

    warnDev(
      `Cell: Column with type ${
        column.type
      } should return a string or number for sorting, but received: ${typeof valueA} and ${typeof valueB}`
    )
    return 0
  })
}
